import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { INPUT_DEBOUNCE_TIMER } from '@telialt/utils/src/eshopConstants';

import { IncrementInputProps } from './model';

const formatValue = (value: number) => {
    return parseFloat(value.toFixed(2));
};

export const useIncrementInputControls = ({
    minValue,
    maxValue,
    paymentInCart,
    onChange,
    incrementStep,
}: IncrementInputProps) => {
    const verifyRange = useCallback(
        (value: number) => {
            if (value < minValue) {
                return minValue;
            }
            if (value > maxValue) {
                return maxValue;
            }

            return value ?? minValue;
        },
        [maxValue, minValue],
    );

    const checkIsValid = (value: number) => {
        return verifyRange(value) === value;
    };

    const [isDirty, setIsDirty] = useState(paymentInCart !== minValue);
    const [inputValue, setInputValue] = useState(verifyRange(paymentInCart));
    const triggerOnChange = useMemo(() => _.debounce(onChange, INPUT_DEBOUNCE_TIMER), [onChange]);

    // Keeping reference to value to use in effect without subscribing to changes
    const valueRef = useRef(inputValue);
    valueRef.current = inputValue;

    // If provided value `paymentInCart` is not valid - calling onChange with valid value immediately
    useEffect(() => {
        if (minValue > maxValue) {
            const datadogLogs = datadogLogger();
            datadogLogs.warn('Increment Input Controls: illogical data', {
                minValue,
                maxValue,
                component: DDComponent.EshopProductCore,
            });

            return /* do not call onChange, if BE sends illogical data */;
        }
        const verifiedPaymentInCart = formatValue(verifyRange(paymentInCart));
        if (paymentInCart !== verifiedPaymentInCart) {
            setIsDirty(false);
            onChange(verifiedPaymentInCart);
            setInputValue(verifiedPaymentInCart);
        }
    }, [onChange, paymentInCart, verifyRange]);

    // Change initial value on range change if it was not modified by user yet
    useEffect(() => {
        if (isDirty) {
            return;
        }

        const formattedMinValue = formatValue(minValue);
        if (valueRef.current !== formattedMinValue) {
            setInputValue(formattedMinValue);
            onChange(formattedMinValue);
        }
    }, [isDirty, minValue, onChange]);

    useEffect(() => {
        return () => {
            triggerOnChange.cancel();
        };
    }, [triggerOnChange]);

    useEffect(() => {
        if (inputValue > maxValue) {
            const value = formatValue(maxValue);
            setInputValue(value);
            setIsDirty(true);
            triggerOnChange(value);
        }
    }, [maxValue]);

    const handleDecrementClick = () => {
        const value = formatValue(verifyRange(inputValue - incrementStep));
        if (inputValue !== value) {
            setInputValue(value);
            setIsDirty(true);
            triggerOnChange(value);
        }
    };

    const handleIncrementClick = () => {
        const value = formatValue(verifyRange(inputValue + incrementStep));
        if (inputValue !== value) {
            setInputValue(value);
            setIsDirty(true);
            triggerOnChange(value);
        }
    };

    const handleInputChange = <Ev extends SyntheticEvent<HTMLInputElement>>(event: Ev) => {
        const inputElement =
            (event.currentTarget instanceof HTMLInputElement && event.currentTarget) ||
            (event.target instanceof HTMLInputElement && event.target);
        const value = Number(inputElement.value);
        const formattedValue = isNaN(value) ? paymentInCart : formatValue(value);
        setInputValue(formattedValue);
        if (checkIsValid(formattedValue)) {
            setIsDirty(true);
            triggerOnChange(formattedValue);
        } else {
            triggerOnChange.cancel();
        }
    };

    return {
        inputValue,
        handleOutsideClick: () => setInputValue(paymentInCart),
        handleDecrementClick,
        handleIncrementClick,
        handleInputChange,
    };
};
